import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { NeonBackground } from "../components/NeonBackground";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../context/AuthContext";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setInfo("Se l'email è registrata, riceverai a breve un link per reimpostare la password. Controlla anche lo spam.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Operazione non riuscita.");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setInfo(""); };

  return (
    <div className="noise-overlay min-h-screen flex items-center justify-center px-5">
      <NeonBackground />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-black/60 backdrop-blur-xl border border-white/10 rounded-sm p-8 sm:p-10 animate-glow-breathe">
        <div className="flex justify-center mb-6">
          <img src="/logo-web.png" alt="AC Creator 3D" className="h-20 w-auto animate-logo" />
        </div>

        {mode === "login" ? (
          <>
            <h1 className="font-display font-bold text-2xl tracking-tight text-white">Area amministratore</h1>
            <p className="text-sm text-muted-foreground font-light mt-2 mb-8">Accedi per gestire richieste e galleria.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Email</Label>
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@esempio.it" className="fld" />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="fld" />
              </div>
              {error && <p data-testid="login-error" className="text-sm text-neon-pink font-mono">{error}</p>}
              <Button data-testid="login-submit-btn" type="submit" disabled={loading}
                className="w-full h-12 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 transition-colors duration-200 disabled:opacity-60">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Accesso…</> : "Accedi"}
              </Button>
            </form>
            <button data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
              className="mt-5 w-full text-center text-sm font-mono text-muted-foreground hover:text-neon-cyan transition-colors duration-200">
              Password dimenticata?
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display font-bold text-2xl tracking-tight text-white">Recupera password</h1>
            <p className="text-sm text-muted-foreground font-light mt-2 mb-8">Inserisci la tua email: ti invieremo un link per reimpostare la password.</p>
            {info ? (
              <div data-testid="forgot-info" className="flex items-start gap-3 p-4 rounded-sm border border-neon-green/40 bg-neon-green/5 text-sm text-white font-light">
                <MailCheck className="w-5 h-5 text-neon-green shrink-0 mt-0.5" /> <span>{info}</span>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-5">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Email</Label>
                  <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@esempio.it" className="fld" />
                </div>
                {error && <p data-testid="forgot-error" className="text-sm text-neon-pink font-mono">{error}</p>}
                <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                  className="w-full h-12 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 transition-colors duration-200 disabled:opacity-60">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Invio…</> : "Invia link di reset"}
                </Button>
              </form>
            )}
            <button data-testid="back-to-login" onClick={() => switchMode("login")}
              className="mt-5 w-full flex items-center justify-center gap-2 text-sm font-mono text-muted-foreground hover:text-neon-cyan transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" /> Torna all'accesso
            </button>
          </>
        )}

        <Link to="/" data-testid="login-back-home"
          className="mt-6 flex items-center justify-center gap-2 text-sm font-mono text-muted-foreground hover:text-neon-cyan transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> Torna alla Home
        </Link>
      </motion.div>
      <style>{`.fld{background:hsl(var(--input));border-color:hsl(var(--border));color:white}.fld:focus-visible{outline:none;box-shadow:0 0 0 2px #00F0FF}`}</style>
    </div>
  );
}
